import { LoadingOutlined } from '@ant-design/icons';
import { Observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { ROUTES } from '../../../../common/constants/routes.const';
import { getData } from '../../../../common/utils/network.utils';
import { useTrackEvent, useTrackGTMEvent } from '../../../../hooks/useTrackEvent/useTrackEvent';
import { VipDocuSignIframeWrapper, VipSyncIntroText } from '../../../add-item/components/AddItem.styles';
import { InstitutionSearchInputAndResults } from '../../../add-item/components/InstitutionSearchInputAndResults';
import { AddItemAkoyaAccountsStep } from '../../../add-item/components/steps/AddItemAkoyaAccountStep';
import { AddItemPlaidAccountsStep } from '../../../add-item/components/steps/AddItemPlaidAccountStep';
import { AddItemSaltedgeAccountsStep } from '../../../add-item/components/steps/AddItemSaltedgeAccountStep';
import { VipSyncManualConsent } from '../../../add-item/components/steps/VipSyncFlow/steps-vip-sync/VipManualConsent';
import { VipPendingModal } from '../../../add-item/components/steps/VipSyncFlow/steps-vip-sync/VipPendingModal';
import { VipSyncIntro, VipSyncSelectedInstitutionRow } from '../../../add-item/components/steps/VipSyncFlow/steps-vip-sync/VipSyncIntro';
import { ADD_ITEM_STEP } from '../../../add-item/data/create-item.store';
import { useAnalytics } from '../../../app/data/AnalyticsProvider';
import { useStore } from '../../../app/data/root.store';
import { errorNotification } from '../../../layout/components/Notifications/notifications';
import { ArrowDownIcon, AutoTrackIcon, LearnSyncCashAccountIcon, OnlineSyncIcon, SecurityIcon } from '../../assets/userActivationIcons';
import { AnimatedText } from '../ChatRow/AnimatedText';
import { ChatRow } from '../ChatRow/ChatRow';
import { ActionButton, ActionButtonsWrapper, ActivationFlowStepWrapper, AnimatedActivationFlowWrapper, LearnPromptButton, LearnPromptHoverEffect, LearnPromptWrapper, MenuItemWrapper, MenuTitle, MenuWrapper, PlaidProviderImage, ProvidersWrapper, SaltedgeProviderImage, StepContent, StepFooter, WhatYouGetMenuItemIcon, WhatYouGetMenuItemSubTitle, WhatYouGetMenuItemTextContent, WhatYouGetMenuItemTitle, WhatYouGetMenuItemWrapper } from '../UserActivationFlowPage.styles';
import { useAnimationsStepDelayedHook } from '../useAnimationsStepDelayedHook';

const vipSyncActionButtonTextByIndex = {
  '0_manual': 'Continue',
  '0_docusign': 'Continue',
  '1_manual': 'Login & Continue',
  '1_docusign': 'Review & Sign',
  '2_manual': 'Confirm & Continue',
  2: 'Done',
}

const vipMixpanelByIndex = {
  '0_manual': {
    cta_text: 'Continue',
    destination: 'Service Agreement',
    step_name: 'Service Overview',
    step_number: 1,
  },
  '0_docusign': {
    cta_text: 'Continue',
    destination: 'Service Agreement',
    step_name: 'Service Overview',
    step_number: 1,
  },
  '1_manual': {
    cta_text: 'Login & Continue',
    destination: 'Service Agreement',
    step_name: 'Service Agreement',
    step_number: 2,
  },
  '1_docusign': {
    cta_text: 'Review & Sign',
    destination: 'Service Agreement',
    step_name: 'Service Agreement',
    step_number: 2,
  },
  '2_manual': {
    cta_text: 'Confirm & Continue',
    destination: 'Success Screen',
    step_name: 'Service Agreement',
    step_number: 3,
  },
  2: {
    cta_text: 'Done',
    destination: 'Wealth Overview',
    step_name: 'Success Screen',
    step_number: 4,
  }
}

export const SyncCashAccountStep = (props) => {
  const { uiStore, createItemStore, userStore, magicBoxStore, magicBoxAiStore, itemStore } = useStore();
  const animationStep = useAnimationsStepDelayedHook({ animationsStepDelayed: [700] });
  const [isLoadingAddingAccount, setIsLoadingAddingAccount] = useState(false);
  const [vipSyncStepIndex, setVipSyncStepIndex] = useState(-1);
  const [documentToSignUrl, setDocumentToSignUrl] = useState(null);
  const [isDocumentToSignUrlLoading, setIsDocumentToSignUrlLoading] = useState(false);
  const [isShowDocuSignIframe, setIsShowDocuSignIframe] = useState(false);
  const [isVipPendingModalOpen, setIsVipPendingModalOpen] = useState(false);
  const [isConsentApproved, setIsConsentApproved] = useState(false);
  const [trackedSteps, setTrackedSteps] = useState([]);
  const { trackEvent } = useAnalytics()
  const mixpanelStepsData = { step_name: 'Sync your accounts', step_number: 3 };

  useEffect(() => {
    createItemStore.setInstitutionStep('bank');
    return () => { }
  }, [createItemStore])

  const trackStepView = (stepIndex) => {
    if (trackedSteps.includes(stepIndex)) return;
    
    let stepData;
    if (stepIndex === 0) {
      stepData = {
        step_name: 'Service Overview',
        step_number: 1
      };
    } else if (stepIndex === 1) {
      stepData = {
        step_name: 'Service Agreement',
        step_number: 2
      };
    } else if (stepIndex === 2) {
      stepData = {
        step_name: 'Success Screen',
        step_number: 3
      };
    }
    
    if (stepData) {
      trackEvent('VIP Sync Service Viewed', {
        ...stepData,
        institution: createItemStore.selectedVipSyncInstitution?.name,
        url: window.location.href
      });
      setTrackedSteps(prev => [...prev, stepIndex]);
    }
  };

  const handleVipSyncStepChange = (newStepIndex) => {
    setVipSyncStepIndex(newStepIndex);
    trackStepView(newStepIndex);
  };

  const handleContinueOnClick = async (skip) => {
    if (skip) {
      trackEvent('Onboarding Step Completed', mixpanelStepsData);
      await uiStore.setWelcomeDone();
      window.location.href = ROUTES.wealthOverview.url;
      return;
    }
    
    if (vipSyncStepIndex !== -1) {
      const buttonIndex = vipSyncStepIndex + (vipSyncStepIndex === 0 || vipSyncStepIndex === 1 ?
        `_${createItemStore.selectedVipSyncInstitution?.vipAccessRequirement === 'manual' ? 'manual' : 'docusign'}` :
        ``);

      trackEvent('CTA Clicked', {
        cta_text: vipMixpanelByIndex[buttonIndex]?.cta_text,
        destination: vipMixpanelByIndex[buttonIndex]?.destination,
        location: 'VIP Sync Service',
        type: 'Button',
        institution: createItemStore.selectedVipSyncInstitution.name,
      });

      if (vipSyncStepIndex === 1 && createItemStore.selectedVipSyncInstitution?.vipAccessRequirement !== 'manual') {
        setIsDocumentToSignUrlLoading(true);
        try {
          const docUrlResponse = await getData(`docu-sign/create-envelope/${createItemStore.selectedVipSyncInstitution.id}`);
          setIsShowDocuSignIframe(true);
          setDocumentToSignUrl(docUrlResponse.data.docUrl);
        } catch (error) {
          errorNotification('Something went wrong, please contact support');
          console.error('Error fetching document to sign URL:', error);
        } finally {
          setIsDocumentToSignUrlLoading(false);
        }
        return;
      }

      if (vipSyncStepIndex === 2) {
        if (createItemStore.selectedVipSyncInstitution.vipAccessRequirement === 'manual') {
          if (!isConsentApproved) return;

          createItemStore.createVipAsset(() => {
            itemStore.setItemUpdated(true);
            setIsVipPendingModalOpen(true);
          });
          return;
        }
      }
      
      if (vipSyncStepIndex === 3) {
        uiStore.setWelcomeDone();
        window.location.href = ROUTES.wealthOverview.url;
        return;
      }
      
      handleVipSyncStepChange(vipSyncStepIndex + 1);
    }
    
    if (!createItemStore.hasAnyProviderAccounts) return;
    await uiStore.setWelcomeDone();
    if (createItemStore.step === ADD_ITEM_STEP.PLAID_ACCOUNTS) {
      setIsLoadingAddingAccount(!createItemStore.hasAnyInvalidAccountSelection);
      createItemStore.plaidAccountsContinue((data) => {
        trackEvent('Onboarding Step Completed', mixpanelStepsData);
        window.location.href = ROUTES.wealthOverview.url;
      })
    } else if (createItemStore.step === ADD_ITEM_STEP.SALTEDGE_ACCOUNTS) {
      setIsLoadingAddingAccount(!createItemStore.hasAnyInvalidAccountSelection);
      createItemStore.saltedgeAccountsContinue((data) => {
        trackEvent('Onboarding Step Completed', mixpanelStepsData);
        window.location.href = ROUTES.wealthOverview.url;
      })
    } else if (createItemStore.step === ADD_ITEM_STEP.AKOYA_ACCOUNTS) {
      setIsLoadingAddingAccount(!createItemStore.hasAnyInvalidAccountSelection);
      createItemStore.akoyaAccountsContinue((data) => {
        trackEvent('Onboarding Step Completed', mixpanelStepsData);
        window.location.href = ROUTES.wealthOverview.url;
      })
    }
  }

  const handleBackButtonOnClick = () => {
    if (vipSyncStepIndex > -1) {
      const newStepIndex = vipSyncStepIndex === 0 ? -1 : 
        [1, 2].includes(vipSyncStepIndex) ? 0 : 
        vipSyncStepIndex - 1;
      
      if (newStepIndex >= 0) {
        handleVipSyncStepChange(newStepIndex);
      } else {
        setVipSyncStepIndex(newStepIndex);
      }
      return;
    }
    if (createItemStore.isAnyProviderInProcess) return;

    if (userStore.isWelcome && magicBoxAiStore.switchToManualReview && !magicBoxStore.hasAnyFileUpload) {
      magicBoxAiStore.setSwitchToManualReview(false);
    }

    uiStore.setActivationStepIndex(uiStore.activationStepIndex - 1);
  }

  useTrackEvent('Onboarding Step Viewed', mixpanelStepsData);
  useTrackGTMEvent('Onboarding Step Viewed', mixpanelStepsData);

  const isWithoutInstitutionSearchInputAndResults = [ADD_ITEM_STEP.PLAID_ACCOUNTS, ADD_ITEM_STEP.SALTEDGE_ACCOUNTS, ADD_ITEM_STEP.AKOYA_ACCOUNTS]

  useEffect(() => {
    const handleDocuSignMessage = (event) => {
      if (event.data && event.data.type === 'DOCUSIGN_COMPLETE') {
        setIsShowDocuSignIframe(false);

        if (event.data.status === 'success') {
          createItemStore.createVipAsset(() => {
            itemStore.setItemUpdated(true);
            setIsVipPendingModalOpen(true);
          });
        } else if (event.data.status === 'cancelled') {
          errorNotification('Document signing was cancelled');
        } else if (event.data.status === 'error') {
          errorNotification('An error occurred during document signing');
        }
      }
    };

    window.addEventListener('message', handleDocuSignMessage);

    return () => {
      window.removeEventListener('message', handleDocuSignMessage);
    };
  }, [createItemStore, itemStore, trackEvent, createItemStore.selectedVipSyncInstitution]);

  const handleVipPendingModalDone = async () => {
    setIsVipPendingModalOpen(false);
    await uiStore.setWelcomeDone();
    window.location.href = ROUTES.wealthOverview.url;
  };

  return (<Observer>{() => (
    <ActivationFlowStepWrapper>
      <StepContent withFixedActions>
        {vipSyncStepIndex < 1 ? <ChatRow duration={0}>
          <span>Sync your bank account to track the inflow and outflow of your investments.</span>
        </ChatRow> : null}

        {!isWithoutInstitutionSearchInputAndResults.includes(createItemStore.step) && vipSyncStepIndex === -1 && (<AnimatedActivationFlowWrapper padding={uiStore.isDesktopView ? '16px 0 16px 62px' : '16px 0px'}><InstitutionSearchInputAndResults isActivationFlow setVipSyncStepIndex={() => handleVipSyncStepChange(0)} /></AnimatedActivationFlowWrapper>)}
        {createItemStore.step === ADD_ITEM_STEP.PLAID_ACCOUNTS && <AnimatedActivationFlowWrapper padding={uiStore.isDesktopView ? '16px 0px 16px 62px' : '16px 0px'}><AddItemPlaidAccountsStep isActivationFlow onFinish={(metadataStore) => { userStore.updateData(); metadataStore.fetchData(); }} /></AnimatedActivationFlowWrapper>}
        {createItemStore.step === ADD_ITEM_STEP.SALTEDGE_ACCOUNTS && <AnimatedActivationFlowWrapper padding={uiStore.isDesktopView ? '16px 0 16px 62px' : '16px 0'}><AddItemSaltedgeAccountsStep isActivationFlow onFinish={(metadataStore) => { userStore.updateData(); metadataStore.fetchData(); }} /></AnimatedActivationFlowWrapper>}
        {createItemStore.step === ADD_ITEM_STEP.AKOYA_ACCOUNTS && <AnimatedActivationFlowWrapper padding={uiStore.isDesktopView ? '16px 0 16px 62px' : '16px 0'}><AddItemAkoyaAccountsStep isActivationFlow onFinish={(metadataStore) => { userStore.updateData(); metadataStore.fetchData(); }} /></AnimatedActivationFlowWrapper>}
        {vipSyncStepIndex === 0 && <><AnimatedActivationFlowWrapper padding={uiStore.isDesktopView ? '16px 0 16px 62px' : '16px 0'}>
          <VipSyncSelectedInstitutionRow item={createItemStore.selectedVipSyncInstitution} isFullWidth />
        </AnimatedActivationFlowWrapper>
          <ChatRow>
            <VipSyncIntroText style={{ padding: '0', textAlign: 'start', maxWidth: '100%' }}>
              Our VIP Syncing Service requires <span style={{ color: '#1A1B1D', fontWeight: '700' }}>read-only</span> access to your account.
              <br />
              <br />
              Please note that this setup process may take some time, depending on how quickly {createItemStore.selectedVipSyncInstitution.name} responds. Once complete, your account will auto-sync with Vyzer's VIP service.
              <br />
              <br />
              Hit 'Continue' to sync this account.
            </VipSyncIntroText>
          </ChatRow>
        </>}
        {vipSyncStepIndex === 1 && <>
          <ChatRow>
            {createItemStore.selectedVipSyncInstitution?.vipAccessRequirement === 'manual'
              ? "Please grant read-only access to your account"
              : "Please approve permission for read-only mode access to your account"}
          </ChatRow>
          <ChatRow textOnly customTextWrapperStyle={{ backgroundColor: '#fff' }}>
            <VipSyncSelectedInstitutionRow item={createItemStore.selectedVipSyncInstitution} isFullWidth withoutPadding resetWhiteSpace />
            <VipSyncIntro isActivationFlow item={createItemStore.selectedVipSyncInstitution} />
          </ChatRow>
        </>}
        {vipSyncStepIndex === 2 && <>
          <ChatRow>
            <VipSyncManualConsent isConsentApproved={isConsentApproved} setIsConsentApproved={setIsConsentApproved} userStore={userStore} isActivationFlow item={createItemStore.selectedVipSyncInstitution} />
          </ChatRow>
        </>}
        {isShowDocuSignIframe && <VipDocuSignIframeWrapper>
          <iframe src={documentToSignUrl} width="100%" height="100%" title="Vyzer Service Agreement"></iframe>
        </VipDocuSignIframeWrapper>}

        {createItemStore.isAnyProviderInProcess ? <>
          <ChatRow>
            <span>Your account is being synced to Vyzer.</span>
            <span style={{ color: '#FF0000' }}> Do not close your browser.</span>
          </ChatRow>
          <ChatRow>
            <span>This process may take some time.</span>
          </ChatRow>
        </> : null}

        {createItemStore.isInstitutionConnectFail && !createItemStore.isAnyProviderInProcess ? <ChatRow>
          <AnimatedText delay={1000} text={`Sorry ${userStore.firstNameCapitalize}, it looks like something went wrong and we couldn't sync to your account.
Please try again. Remember, do not close your browser until your account has successfully synced.`} />
        </ChatRow> : null}

        {createItemStore.hasAnyProviderAccounts ? <ChatRow>
          <AnimatedText delay={1000} text={`Great! We've successfully synced your account.`} />
        </ChatRow> : null}

        {animationStep > 0 && !createItemStore.isAnyProviderInProcess && !createItemStore.hasAnyProviderAccounts && !createItemStore.isInstitutionConnectFail && vipSyncStepIndex === -1 ? <LearnPromptWrapper>
          <LearnPromptHoverEffect>
            <LearnPromptButton onClick={() => { props.showLearnMore && props.showLearnMore(true) }}>
              <LearnSyncCashAccountIcon />
              <span>Learn more about syncing</span>
              <ArrowDownIcon />
            </LearnPromptButton>
          </LearnPromptHoverEffect>
        </LearnPromptWrapper> : null}

      </StepContent>

      <StepFooter>
        <ActionButtonsWrapper>
          <ActionButton onClick={handleBackButtonOnClick}>Back</ActionButton>
          {vipSyncStepIndex === -1 ? <ActionButton onClick={() => handleContinueOnClick(true)} style={{ marginLeft: 'auto' }}>Skip</ActionButton> : null}
          <ActionButton
            disabled={vipSyncStepIndex === -1 || (vipSyncStepIndex === 2 && createItemStore.selectedVipSyncInstitution?.vipAccessRequirement === 'manual' && !isConsentApproved)}
            onClick={() => handleContinueOnClick(false)}
            style={{ marginLeft: vipSyncStepIndex === -1 ? '16px' : 'auto' }}
            black
            withIcon={vipSyncStepIndex === 0 && createItemStore.selectedVipSyncInstitution?.vipAccessRequirement === 'docusign'}
          >
            {isLoadingAddingAccount || isDocumentToSignUrlLoading ? <LoadingOutlined /> :
              vipSyncStepIndex !== -1 ?
                vipSyncActionButtonTextByIndex[vipSyncStepIndex + (vipSyncStepIndex === 0 || vipSyncStepIndex === 1 ?
                  `_${createItemStore.selectedVipSyncInstitution?.vipAccessRequirement === 'manual' ? 'manual' : 'docusign'}` :
                  ``)] :
                'Continue'}
            {vipSyncStepIndex === 0 && createItemStore.selectedVipSyncInstitution?.vipAccessRequirement === 'docusign' && <ArrowDownIcon />}
          </ActionButton>
        </ActionButtonsWrapper>
      </StepFooter>

      {(uiStore.isDesktopView && animationStep > 0) && <LearnPromptWrapper>
        <LearnPromptButton>
          <LearnSyncCashAccountIcon />
          Learn
        </LearnPromptButton>
        <LearnPromptHoverEffect><LearnMenu /></LearnPromptHoverEffect>
      </LearnPromptWrapper>}

      {isVipPendingModalOpen && <VipPendingModal item={createItemStore.selectedVipSyncInstitution} onDone={handleVipPendingModalDone} isActivationFlow />}
    </ActivationFlowStepWrapper>
  )}</Observer>)
}

const LearnMenu = (props) => {
  return (
    <MenuWrapper>
      <MenuTitle>Why sync your bank accounts?</MenuTitle>

      <MenuItemWrapper>
        <WhatYouGetMenuItemWrapper>
          <WhatYouGetMenuItemIcon>
            <OnlineSyncIcon />
          </WhatYouGetMenuItemIcon>
          <WhatYouGetMenuItemTextContent>
            <WhatYouGetMenuItemTitle>View your most current balances</WhatYouGetMenuItemTitle>
            <WhatYouGetMenuItemSubTitle>Your Vyzer account will always display the most up-to-date numbers for your balances.</WhatYouGetMenuItemSubTitle>
          </WhatYouGetMenuItemTextContent>
        </WhatYouGetMenuItemWrapper>
      </MenuItemWrapper>

      <MenuItemWrapper>
        <WhatYouGetMenuItemWrapper>
          <WhatYouGetMenuItemIcon>
            <AutoTrackIcon />
          </WhatYouGetMenuItemIcon>
          <WhatYouGetMenuItemTextContent>
            <WhatYouGetMenuItemTitle>Track your performance</WhatYouGetMenuItemTitle>
            <WhatYouGetMenuItemSubTitle>Transactions will be automatically linked to relevant assets, so you will always have an idea of how your investments are performing.</WhatYouGetMenuItemSubTitle>
          </WhatYouGetMenuItemTextContent>
        </WhatYouGetMenuItemWrapper>
      </MenuItemWrapper>

      <MenuItemWrapper>
        <WhatYouGetMenuItemWrapper>
          <WhatYouGetMenuItemIcon>
            <SecurityIcon />
          </WhatYouGetMenuItemIcon>
          <WhatYouGetMenuItemTextContent>
            <WhatYouGetMenuItemTitle>Secure data syncing</WhatYouGetMenuItemTitle>
            <WhatYouGetMenuItemSubTitle>We use secure integrators to ensure that your data is kept safe.</WhatYouGetMenuItemSubTitle>
            <ProvidersWrapper>
              <PlaidProviderImage />
              <SaltedgeProviderImage />
            </ProvidersWrapper>
          </WhatYouGetMenuItemTextContent>
        </WhatYouGetMenuItemWrapper>
      </MenuItemWrapper>

    </MenuWrapper>
  )
}